import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { imgUrl } from "@/lib/api";

const OCCASIONS = {
  birthday: {
    title: "Make their birthday unforgettable",
    kicker: "Birthday Gifting",
    subtitle: "Cakes hand-baked to be candle-worthy, bouquets that outshine the balloons, and hampers that keep the smile going long after the song ends.",
    image: "https://images.unsplash.com/photo-1558636508-e0db3814bd1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    accent: "#8C3A3A",
  },
  anniversary: {
    title: "For the love that keeps rising",
    kicker: "Anniversary Gifting",
    subtitle: "Signature layered cakes, peonies in blush, and premium hampers curated for the ones who count years in memories, not months.",
    image: "https://images.unsplash.com/photo-1509937528035-ad76254b0356?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    accent: "#8C3A3A",
  },
  valentine: {
    title: "Say it in roses, red velvet & rose gold.",
    kicker: "Valentine's Day",
    subtitle: "Twelve red roses, a red velvet dream, and a photo mug that turns Feb 14 into something they'll keep on the shelf all year.",
    image: "https://images.unsplash.com/photo-1518895949257-7621c3c786d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    accent: "#8C3A3A",
  },
  rakhi: {
    title: "Home is a hamper away.",
    kicker: "Raksha Bandhan",
    subtitle: "Cheerful bouquets, sweet-and-savoury boxes and personalised mugs - for the sibling across the country who deserves more than a video call.",
    image: "https://images.unsplash.com/photo-1470309864661-68328b2cd0a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    accent: "#8C3A3A",
  },
};

export default function Occasion() {
  const { slug } = useParams();
  const meta = OCCASIONS[slug];
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    api.get(`/products?occasion=${slug}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [slug, meta]);

  if (!meta) return <Navigate to="/" replace />;

  return (
    <div>
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden">
        <img src={meta.image} alt={meta.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/70" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-16 md:pb-20 text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-white/85" data-testid="occasion-kicker">{meta.kicker}</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.05] mt-4 max-w-3xl">{meta.title}</h1>
            <p className="mt-5 max-w-xl text-white/85 text-lg font-light">{meta.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="overline">Handpicked</div>
            <h2 className="font-display text-3xl md:text-5xl mt-2">Curated for the day</h2>
          </div>
          <div className="flex gap-2 text-sm">
            {Object.keys(OCCASIONS).map((s) => (
              <Link key={s} to={`/occasions/${s}`} data-testid={`occasion-pill-${s}`}
                className={`px-4 py-2 rounded-full border ${s === slug ? "bg-[#2D2A26] text-white border-[#2D2A26]" : "bg-white border-[#E5DFD3] hover:border-[#2D2A26]"}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </Link>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center text-[#6C665F] py-20">Loading curation…</div>
        ) : products.length === 0 ? (
          <div className="text-center text-[#6C665F] py-20">
            <p className="mb-4">No products tagged for this occasion yet.</p>
            <Link to="/shop" className="btn-primary inline-flex">Browse Everything <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="occasion-grid">
            {products.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 8) * 0.04 }}>
                <Link to={`/product/${p.id}`} className="group block" data-testid={`occasion-product-${p.id}`}>
                  <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-[#F4F2EC]">
                    <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover product-img" />
                  </div>
                  <div className="mt-4">
                    <div className="overline">{p.category}</div>
                    <div className="flex justify-between items-baseline mt-1">
                      <div className="font-display text-lg">{p.name}</div>
                      <div className="font-semibold">₹{p.price}</div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
